// src/services/team.ts
//
// Team-domain service. Covers the nutritionist team roster + the RBAC surface
// around it: listing nutritionists with assigned roles, viewing/editing a
// single nutritionist's role + permissions, the role-level permission editor
// (per-role save / enable-all / disable-all), creating new permissions, the
// add/update nutritionist multipart uploads, and the master dropdown lists
// (states, document/qualification/expertise/certification/language/job-type/
// role/reporting-manager) feeding those forms.
//
// Re-exports `getNutritionistFullDetails` from the auth service so callers in
// this domain can stay within `services/team`.

import { apiFetch } from "../Component/api/apiFetch";
import { API_URLS } from "../Component/api/url";
import { handleJson } from "./http";

export { getNutritionistFullDetails } from "./auth";

export const getNutritionistsListWithAssignRole = (
  role: string = "",
  status: string = "",
  page: number = 1,
  size: number = 10,
  name: string = ""
): Promise<any> =>
  handleJson(
    apiFetch(
      API_URLS.GET_NUTRITIONISTS_LIST_WITH_ASSIGN_ROLE(
        role,
        status,
        page,
        size,
        name
      ),
      { method: "GET" }
    )
  );

export const getTemporaryRolesAndPermissionsDropdown = (): Promise<any> =>
  handleJson(
    apiFetch(API_URLS.GET_TEMPORARY_ROLES_AND_PERMISSIONS_DROPDOWN_VALUES, {
      method: "GET",
    })
  );

export const getNutritionistDetailsWithRole = (
  nutritionistId: string | number
): Promise<any> =>
  handleJson(
    apiFetch(API_URLS.GET_NUTRITIONIST_DETAILS_WITH_ROLE(nutritionistId), {
      method: "GET",
    })
  );

export const getPermissionsListWithRole = (
  nutritionistId: string | number,
  role: string
): Promise<any> =>
  handleJson(
    apiFetch(API_URLS.GET_PERMISSIONS_LIST_WITH_ROLE(nutritionistId, role), {
      method: "GET",
    })
  );

export const assignRoleToNutritionist = (
  payload: Record<string, any>
): Promise<any> =>
  handleJson(
    apiFetch(API_URLS.ASSIGN_ROLE_TO_NUTRITIONIST, {
      method: "POST",
      body: JSON.stringify(payload),
    })
  );

export const getPermissionsListForRole = (role: string): Promise<any> =>
  handleJson(
    apiFetch(API_URLS.GET_PERMISSIONS_LIST_FOR_ROLE(role), { method: "GET" })
  );

export const saveAssignedPermissionsToRole = (
  role: string,
  payload: Record<string, any>
): Promise<any> =>
  handleJson(
    apiFetch(API_URLS.SAVE_ASSIGNED_PERMISSIONS_TO_ROLE(role), {
      method: "POST",
      body: JSON.stringify(payload),
    })
  );

export const enableAllPermissionsForRole = (
  role: string,
  payload: Record<string, any>
): Promise<any> =>
  handleJson(
    apiFetch(API_URLS.ENABLE_ALL_PERMISSIONS_FOR_ROLE(role), {
      method: "POST",
      body: JSON.stringify(payload),
    })
  );

export const disableAllPermissionsForRole = (
  role: string,
  payload: Record<string, any>
): Promise<any> =>
  handleJson(
    apiFetch(API_URLS.DISABLE_ALL_PERMISSIONS_FOR_ROLE(role), {
      method: "POST",
      body: JSON.stringify(payload),
    })
  );

export const createNewPermission = (
  payload: Record<string, any>
): Promise<any> =>
  handleJson(
    apiFetch(API_URLS.CREATE_NEW_PERMISSION, {
      method: "POST",
      body: JSON.stringify(payload),
    })
  );

export const addNewNutritionist = (formData: FormData): Promise<any> =>
  handleJson(
    apiFetch(API_URLS.ADD_NEW_NUTRITIONIST, {
      method: "POST",
      body: formData,
    })
  );

export const updateNutritionistDetails = (
  _nutristId: string | number | undefined,
  formData: FormData
): Promise<any> =>
  handleJson(
    apiFetch(API_URLS.UPDATE_NUTRITIONIST_DETAILS, {
      method: "POST",
      body: formData,
    })
  );

export const getStateList = (): Promise<any> =>
  handleJson(apiFetch(API_URLS.GET_STATE_LIST, { method: "GET" }));

export const getDocumentTypes = (): Promise<any> =>
  handleJson(apiFetch(API_URLS.GET_DOCUMENT_TYPES, { method: "GET" }));

export const getQualificationTypes = (): Promise<any> =>
  handleJson(apiFetch(API_URLS.GET_QUALIFICATION_TYPES, { method: "GET" }));

export const getSpecializationList = (): Promise<any> =>
  handleJson(apiFetch(API_URLS.GET_SPECIALIZATION_LIST, { method: "GET" }));

export const getExpertiseTypes = (): Promise<any> =>
  handleJson(apiFetch(API_URLS.GET_EXPERTISE_TYPES, { method: "GET" }));

export const getCertificationTypes = (): Promise<any> =>
  handleJson(apiFetch(API_URLS.GET_CERTIFICATION_TYPES, { method: "GET" }));

export const getLanguageList = (): Promise<any> =>
  handleJson(apiFetch(API_URLS.GET_LANGUAGE_LIST, { method: "GET" }));

export const getNutritionistRole = (): Promise<any> =>
  handleJson(apiFetch(API_URLS.GET_NUTRITIONIST_ROLE, { method: "GET" }));

export const getNutritionistJobTypes = (): Promise<any> =>
  handleJson(apiFetch(API_URLS.GET_NUTRITIONIST_JOB_TYPES, { method: "GET" }));

export const getReportingManagerList = (): Promise<any> =>
  handleJson(apiFetch(API_URLS.GET_REPORTING_MANAGER_LIST, { method: "GET" }));
